import { useEffect, useState } from 'react'
import api from '../../api/axios'
import { PaperAirplaneIcon } from '@heroicons/react/24/outline'
import type { Device } from './fleet'

// A custom push to whoever is signed in on this device, whatever version it
// runs — the version reminder only covers phones that are behind. Mainly for
// Android betas, which need a full reinstall from the Play Store. Sent through
// POST /super-admin/devices/:id/message, which goes to every phone the person
// has registered and reports whether any received it.

const TITLE_MAX = 65
const BODY_MAX = 240
// Same fallbacks the server uses when global settings have no store links.
const FALLBACK_STORE = {
  ios: 'https://apps.apple.com/app/id6756016660',
  android: 'https://play.google.com/store/apps/details?id=com.CITS.timeclock',
}

type PresetKey = 'reinstall' | 'update' | 'custom'

function preset(key: PresetKey, device: Device, stores: typeof FALLBACK_STORE) {
  const first = (device.employee_name || '').split(' ')[0]
  const hi = first ? `${first}, ` : ''
  const store = device.platform === 'ios' ? 'App Store' : 'Google Play Store'
  const link = stores[device.platform] || ''
  if (key === 'reinstall')
    return {
      title: 'Please reinstall CITS Time',
      // Uninstalling deletes anything the phone hasn't synced, so they're
      // told to sync first.
      body: `${hi}the test version on your phone has ended. First open CITS Time with signal so everything syncs, then uninstall it and install it again from the ${store}. Tap here to open it.`,
      link,
    }
  if (key === 'update')
    return {
      title: 'Update CITS Time',
      body: `${hi}a newer version of CITS Time is in the ${store}. Tap here to open it and update.`,
      link,
    }
  return { title: '', body: '', link: '' }
}

export default function DeviceMessage({
  device,
  notify,
  onClose,
}: {
  device: Device
  notify: (msg: string, tone?: 'ok' | 'error') => void
  onClose: () => void
}) {
  const [stores, setStores] = useState(FALLBACK_STORE)
  const [kind, setKind] = useState<PresetKey>(device.platform === 'android' ? 'reinstall' : 'update')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [link, setLink] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    api
      .get('/super-admin/global-settings')
      .then((r) => {
        const s = r.data.settings || {}
        const v = (k: string) => (s[k]?.value ? String(s[k].value) : '')
        setStores({ ios: v('ios_store_url') || FALLBACK_STORE.ios, android: v('android_store_url') || FALLBACK_STORE.android })
      })
      .catch(() => {
        /* fall back to the default store links */
      })
  }, [])

  // Fill the fields whenever the preset (or the store links) change.
  useEffect(() => {
    const p = preset(kind, device, stores)
    setTitle(p.title)
    setBody(p.body)
    setLink(p.link)
    setError('')
  }, [kind, device, stores])

  const linkOk = !link || /^https:\/\//i.test(link.trim())
  const canSend = title.trim() && body.trim() && title.length <= TITLE_MAX && body.length <= BODY_MAX && linkOk && !sending

  const send = async () => {
    if (!canSend) return
    setSending(true)
    setError('')
    try {
      const r = await api.post(`/super-admin/devices/${device.id}/message`, { title, body, link: link.trim() || undefined })
      const n = Number(r.data?.sent || 0)
      notify(`Message sent to ${n} phone${n === 1 ? '' : 's'}`)
      onClose()
    } catch (err) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || "Couldn't send the message")
    } finally {
      setSending(false)
    }
  }

  const input = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3" aria-label="Send a message">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Message {device.employee_name || 'this person'}</h3>
          <p className="text-xs text-slate-500">A push notification to every phone they're signed in on, whatever version it runs.</p>
        </div>
      </div>

      <div role="group" aria-label="Message type" className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
        {(
          [
            ['reinstall', 'Reinstall from store'],
            ['update', 'Update from store'],
            ['custom', 'Custom'],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            aria-pressed={kind === k}
            onClick={() => setKind(k)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md ${kind === k ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <label htmlFor="dm-title" className="font-medium text-slate-700">Title</label>
          <span className={title.length > TITLE_MAX ? 'text-red-600' : 'text-slate-400'}>{title.length}/{TITLE_MAX}</span>
        </div>
        <input id="dm-title" className={input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Please reinstall CITS Time" />
      </div>

      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <label htmlFor="dm-body" className="font-medium text-slate-700">Message</label>
          <span className={body.length > BODY_MAX ? 'text-red-600' : 'text-slate-400'}>{body.length}/{BODY_MAX}</span>
        </div>
        <textarea id="dm-body" rows={4} className={`${input} resize-y`} value={body} onChange={(e) => setBody(e.target.value)} />
      </div>

      <div className="space-y-1">
        <label htmlFor="dm-link" className="text-xs font-medium text-slate-700">Link when tapped (optional)</label>
        <input id="dm-link" className={`${input} font-mono text-xs`} value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://play.google.com/store/apps/details?id=…" />
        {!linkOk ? (
          <p className="text-xs text-red-600">The link must start with https://</p>
        ) : (
          <p className="text-xs text-slate-500">Opens on tap on app versions from this release on; older versions just open the app. The text reaches every version.</p>
        )}
      </div>

      {/* Lock-screen preview */}
      <div className="rounded-2xl bg-white/90 border border-slate-200 shadow-sm px-3.5 py-3 flex gap-3">
        <div className="w-9 h-9 rounded-lg bg-orange-500 flex-shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="flex justify-between gap-2 text-[11px] text-slate-500"><span className="font-semibold uppercase tracking-wide">CITS Time</span><span>now</span></div>
          <div className="text-sm font-semibold text-slate-900 truncate">{title || 'Title'}</div>
          <div className="text-[13px] text-slate-700 line-clamp-3">{body || 'Your message'}</div>
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onClose} className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white hover:bg-slate-50">
          Cancel
        </button>
        <button
          type="button"
          disabled={!canSend}
          onClick={send}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          <PaperAirplaneIcon className="w-4 h-4" /> {sending ? 'Sending…' : 'Send message'}
        </button>
      </div>
    </section>
  )
}
